import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import Button from '../../components/ui/Button'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { exportNotifications } from './api/notificationsApi'
import NotificationFilters from './components/NotificationFilters'
import NotificationsTable from './components/NotificationsTable'
import { useNotificationNavigate } from './hooks/useNotificationNavigate'
import { useMarkAllNotificationsRead, useNotifications } from './hooks/useNotifications'
import { useUnreadCount } from './hooks/useUnreadCount'
import type { NotificationStatusFilter } from './types'
import './notifications.css'

/** Anayasa: liste ekranlarında sayfa başına varsayılan 10. */
const PAGE_SIZE = 10

const STATUSES: NotificationStatusFilter[] = ['ALL', 'UNREAD', 'READ']

function parseStatus(raw: string | null): NotificationStatusFilter {
  return STATUSES.find((s) => s === raw) ?? 'ALL'
}

/**
 * Bildirimler sayfası (058 / US3) — kullanıcının **tüm** bildirimleri; durum ve tarih aralığı
 * filtreleriyle, sunucu-taraflı sayfalamayla.
 *
 * <p>Sayfa başlığı sol menüdeki adla **birebir aynı** anahtardan beslenir (anayasa: "başlık = menü
 * adı"). Filtre durumu URL'den (`useSearchParams`) okunur; böylece sayfa paylaşılabilir/yenilenebilir
 * kalır — repodaki liste ekranlarıyla aynı kalıp.
 */
export default function NotificationsPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const [status, setStatus] = useState<NotificationStatusFilter>(() =>
    parseStatus(searchParams.get('status')),
  )
  const [range, setRange] = useState<{ from: string | null; to: string | null }>({
    from: searchParams.get('from'),
    to: searchParams.get('to'),
  })
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)

  const list = useNotifications({
    page: Math.floor(first / rows),
    size: rows,
    status,
    from: range.from ?? undefined,
    to: range.to ?? undefined,
  })

  const openNotification = useNotificationNavigate()
  const unread = useUnreadCount()
  const markAllRead = useMarkAllNotificationsRead()
  const toast = useToast()

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const filtered = status !== 'ALL' || !!range.from || !!range.to
  const unreadCount = unread.data?.count ?? 0

  /**
   * "Tümünü Okundu Olarak İşaretle" — **yıkıcı ve geri alınamaz** bir toplu işlem olduğundan onay
   * adımından geçer (anayasa: "Yıkıcı işlemler onay adımı içerir"; spec FR-034).
   *
   * Onay metni **etkilenecek kayıt sayısını** ve kapsamın filtrelerden bağımsız olduğunu açıkça
   * söyler — kullanıcı, ekranda 3 satır görürken 40 bildirimin işaretleneceğini bilerek onaylar.
   */
  const confirmMarkAllRead = () => {
    confirmDialog({
      header: t('notifications.markAllRead.title'),
      message: t('notifications.markAllRead.message', { count: unreadCount }),
      acceptLabel: t('notifications.markAllRead.accept'),
      accept: () => {
        markAllRead.mutate(undefined, {
          onSuccess: (result) => {
            toast.show({
              severity: 'success',
              summary: t('notifications.markAllRead.done', { count: result.updatedCount }),
              life: 3000,
            })
          },
        })
      },
    })
  }

  /** Herhangi bir filtre değiştiğinde liste **ilk sayfaya** döner (spec FR-031). */
  const changeStatus = (next: NotificationStatusFilter) => {
    setStatus(next)
    setFirst(0)
  }
  const changeRange = (next: { from: string | null; to: string | null }) => {
    setRange(next)
    setFirst(0)
  }
  const clearFilters = () => {
    setStatus('ALL')
    setRange({ from: null, to: null })
    setFirst(0)
  }

  const empty = useMemo(
    () => (
      <EmptyState
        icon="pi pi-bell-slash"
        title={filtered ? t('notifications.emptyFiltered.title') : t('notifications.empty.title')}
        description={
          filtered ? t('notifications.emptyFiltered.desc') : t('notifications.empty.desc')
        }
      />
    ),
    [filtered, t],
  )

  return (
    <>
      <ConfirmDialog />
      <PageHeader
        title={t('nav.notifications.label')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA (kullanici istegi 2026-08-26). Onceden filtre
                cubugundaydi (`NotificationFilters` -> `FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() =>
                exportNotifications({
                  status,
                  from: range.from ?? undefined,
                  to: range.to ?? undefined,
                })
              }
              disabled={items.length === 0}
            />
            <Button
              label={t('notifications.markAllRead.action')}
              icon="pi pi-check"
              outlined
              // Okunmamış yoksa eylem anlamsızdır → pasif (spec FR-035).
              disabled={unreadCount === 0 || markAllRead.isPending}
              loading={markAllRead.isPending}
              onClick={confirmMarkAllRead}
            />
          </>
        }
      />

      <NotificationFilters
        status={status}
        onStatus={changeStatus}
        from={range.from}
        to={range.to}
        onRange={changeRange}
        showClear={filtered}
        onClear={clearFilters}
      />

      <NotificationsTable
        items={items}
        loading={list.isPending}
        totalRecords={total}
        first={first}
        rows={rows}
        onPage={({ first: nextFirst, rows: nextRows }) => {
          setFirst(nextFirst)
          setRows(nextRows)
        }}
        onOpen={openNotification}
        emptyMessage={empty}
      />
    </>
  )
}
